import * as React from 'react'
import classNames from 'classnames'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { choiceToggle } from '../flow/choice'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import type { WithCachedImageUrl } from '../image/WithCachedImage'
import type { TimelineDetails } from './TimelineDetails'
import i18next from 'i18next'
import { fakeFetchUrl } from '../file/withS3Url'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import { InspectableImage } from '../photo/InspectableImage'
import useBreakpoints, { type BreakpointDefinition } from '../util/useBreakpoints'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    choices: Flows.ImageChoice[]
    minChoices: number
    maxChoices: number
    size: Flows.ImageSizeType
    answerValue: Flows.ImageSelectAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.ImageSelectAnswer>
    withCachedImageUrl: WithCachedImageUrl
    allowZoom: boolean
    openOptions?: () => void
}

const breakpointDefinitionsMap: Record<Flows.ImageSizeType, BreakpointDefinition[]> = {
    small: [
        {
            type: 'range',
            min: 450,
            max: 601,
            className: 'Breakpoint__450',
        },
        {
            type: 'range',
            min: 600,
            max: 701,
            className: 'Breakpoint__600',
        },
        {
            type: 'range',
            min: 700,
            max: 801,
            className: 'Breakpoint__700',
        },
        {
            type: 'range',
            min: 800,
            max: 1001,
            className: 'Breakpoint__800',
        },
        {
            type: 'range',
            min: 1000,
            max: 1201,
            className: 'Breakpoint__1000',
        },
        {
            type: 'limit',
            size: 1200,
            condition: '>',
            className: 'Breakpoint__1200',
        },
    ],
    medium: [
        {
            type: 'range',
            min: 600,
            max: 801,
            className: 'Breakpoint__600',
        },
        {
            type: 'range',
            min: 800,
            max: 1001,
            className: 'Breakpoint__800',
        },
        {
            type: 'limit',
            size: 1000,
            condition: '>',
            className: 'Breakpoint__1000',
        },
    ],
    large: [
        {
            type: 'limit',
            size: 800,
            condition: '>',
            className: 'Breakpoint__800',
        },
    ],
}

export const ElementImageSelect = (props: Props) => {
    const wrapperRef = React.useRef<HTMLDivElement>()
    const breakpointClasses = useBreakpoints(wrapperRef, breakpointDefinitionsMap[props.size || 'medium'])

    const getElementSize = () => {
        switch (props.size) {
            case 'small':
                return 'small'
            case 'large':
                return 'large'
            default:
                return 'medium'
        }
    }

    const getMinChoiceWarning = () => {
        const { minChoices } = props

        if (minChoices == null || minChoices === 0) return null

        return minChoices === 1
            ? i18next.t('appcore:element.imageSelect.min.one')
            : i18next.t('appcore:element.imageSelect.min.other', { minChoices })
    }

    const handleClick = (id: Flows.ChoiceId) => {
        const { choices, maxChoices, createAnswer } = props
        const selection = props.answerValue ?? []
        const newSelection = choiceToggle(choices, selection, id)

        // If it is a single select replace the current answer
        if (maxChoices != null && maxChoices === 1) {
            return createAnswer((base) => ({
                ...base,
                type: 'image-select',
                value: selection.includes(id) ? [] : [id],
            }))
        }

        // If already made the maximum amount of choices, ignore
        if (maxChoices != null && maxChoices < newSelection.length) {
            return
        }

        createAnswer((base) => ({
            ...base,
            type: 'image-select',
            value: newSelection,
        }))
    }

    const renderChoices = () => {
        const { choices } = props
        const selection = props.answerValue ?? []

        return choices.map(({ label, id, url, annotationFileId }) => {
            const selected = selection.includes(id)
            return (
                <div
                    key={id}
                    className={classNames(
                        'AppCore__flow__ElementImageSelect__choice',
                        `AppCore__flow__ElementImageSelect__choice--${getElementSize()}`,
                        {
                            'AppCore__flow__ElementImageSelect__choice--selected-with-border':
                                selected && selection.length !== 0,
                            'AppCore__flow__ElementImageSelect__choice--excluded-from-selection':
                                !selected &&
                                selection.length !== 0 &&
                                selection.length >= props.maxChoices &&
                                props.maxChoices != null,
                        }
                    )}
                    onClick={() => handleClick(id)}
                >
                    <div className="AppCore__flow__ElementImageSelect__imageWrapper">
                        {props.withCachedImageUrl(url, (src) =>
                            props.withCachedImageUrl(fakeFetchUrl(annotationFileId), (annotation) =>
                                props.allowZoom ? (
                                    <InspectableImage
                                        deps={props.deps}
                                        title={label}
                                        src={src}
                                        fullSizeSrc={url}
                                        annotation={annotationFileId != null ? annotation : null}
                                        imageClass="AppCore__flow__ElementImageSelect__image"
                                        clickableArea="top-left"
                                    />
                                ) : (
                                    <zaptic-annotated-image
                                        className="AppCore__flow__ElementImageSelect__image"
                                        src={src}
                                        annotation={annotationFileId != null ? annotation : null}
                                    />
                                )
                            )
                        )}
                    </div>
                    <div className="AppCore__flow__ElementImageSelect__label">{label}</div>
                </div>
            )
        })
    }

    const tryRender = () => {
        const { deps, elementId, label, timelineDetails, required, validationMessages } = props

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={props.loopPathIds}
                validationMessages={validationMessages}
                timelineDetails={timelineDetails}
            >
                <ElementLabel
                    required={required}
                    label={label}
                    secondaryLabel={getMinChoiceWarning()}
                    hasBeenEdited={timelineDetails && timelineDetails.hasBeenEdited}
                    language={deps.language}
                    validationMessages={validationMessages}
                    openOptions={props.openOptions}
                />
                <div className={classNames('AppCore__flow__ElementImageSelect', ...breakpointClasses)} ref={wrapperRef}>
                    <div className="AppCore__flow__ElementImageSelect__choices">{renderChoices()}</div>
                </div>
            </ExecutionElement>
        )
    }

    return renderCatch(props.deps, tryRender)
}
